import { WarningIcon } from "@phosphor-icons/react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "./api";
import { Shell, type Section } from "./components/Shell";
import { Toast, type ToastData } from "./components/Toast";
import { ErrorState, SkeletonRows } from "./components/UI";
import { useResource } from "./hooks/useResource";
import { errorText } from "./utils";
import type { ChatTab } from "./pages/ChatDetailPage";
import { DashboardPage } from "./pages/DashboardPage";
import type { ListKind } from "./pages/ListsPage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import type { ChatOptions, SettingDefinition, User } from "./types";

const ChatDetailPage = lazy(() => import("./pages/ChatDetailPage").then((module) => ({ default: module.ChatDetailPage })));
const ChatsPage = lazy(() => import("./pages/ChatsPage").then((module) => ({ default: module.ChatsPage })));
const ImageCachePage = lazy(() => import("./pages/ImageCachePage").then((module) => ({ default: module.ImageCachePage })));
const ListsPage = lazy(() => import("./pages/ListsPage").then((module) => ({ default: module.ListsPage })));
const LogsPage = lazy(() => import("./pages/LogsPage").then((module) => ({ default: module.LogsPage })));
const PatternsPage = lazy(() => import("./pages/PatternsPage").then((module) => ({ default: module.PatternsPage })));
const UserDetailPage = lazy(() => import("./pages/UserDetailPage").then((module) => ({ default: module.UserDetailPage })));
const UsersPage = lazy(() => import("./pages/UsersPage").then((module) => ({ default: module.UsersPage })));
const VotesPage = lazy(() => import("./pages/VotesPage").then((module) => ({ default: module.VotesPage })));

type LoadState = "loading" | "guest" | "ready" | "error";

function currentPath() {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function sectionFor(path: string): Section {
  if (path === "/admin") return "dashboard";
  if (path.startsWith("/chats") || path === "/admin/all-chats") return "chats";
  if (path.startsWith("/users")) return "users";
  if (path.startsWith("/logs") || path === "/admin/logs") return "logs";
  if (path.startsWith("/votes")) return "votes";
  if (path.startsWith("/admin/image-cache")) return "image-cache";
  if (path.startsWith("/admin/patterns")) return "patterns";
  if (path.startsWith("/admin/")) return "lists";
  return "dashboard";
}

function chatTabFromPath(value: string | undefined): ChatTab {
  if (value === "settings" || value === "admins" || value === "bans" || value === "audit") return value;
  if (value === "spam-log") return "spam";
  return "overview";
}

const chatTabPaths: Record<ChatTab, string> = {
  overview: "",
  settings: "/settings",
  admins: "/admins",
  bans: "/bans",
  spam: "/spam-log",
  audit: "/audit"
};

export default function App() {
  const [state, setState] = useState<LoadState>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [chatOptions, setChatOptions] = useState<ChatOptions>({ items: [], total: 0, active: 0, ingress: 0 });
  const chats = chatOptions.items;
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [definitions, setDefinitions] = useState<SettingDefinition[]>([]);
  const [path, setPath] = useState(currentPath);
  const [toast, setToast] = useState<ToastData>(null);
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem("cg-theme");
    return saved ? saved === "dark" : true;
  });

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const currentUser = await api.me(signal);
      const [availableChats, settingDefinitions] = await Promise.all([api.chatOptions("", signal), api.settingDefinitions(signal)]);
      setUser(currentUser);
      setChatOptions(availableChats);
      setDefinitions(settingDefinitions);
      setState("ready");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (error instanceof ApiError && error.status === 401) setState("guest");
      else setState("error");
    }
  }, []);

  useEffect(() => {
    const expired = () => { setUser(null); setState("guest"); };
    window.addEventListener("cg-session-expired", expired);
    return () => window.removeEventListener("cg-session-expired", expired);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("cg-theme", dark ? "dark" : "light");
  }, [dark]);
  useEffect(() => {
    const listener = () => setPath(currentPath());
    window.addEventListener("popstate", listener);
    return () => window.removeEventListener("popstate", listener);
  }, []);
  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  function navigate(nextPath: string) {
    if (currentPath() !== nextPath) window.history.pushState({}, "", nextPath);
    setPath(currentPath());
    window.scrollTo({ top: 0 });
  }

  async function logout() {
    if (loggingOut) return;
    setLoggingOut(true);
    setLogoutError(null);
    try {
      await api.logout();
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 401)) {
        setLogoutError(errorText(error, "Выход не выполнен. Повторите попытку."));
        return;
      }
    } finally {
      setLoggingOut(false);
    }
    setUser(null);
    setChatOptions({ items: [], total: 0, active: 0, ingress: 0 });
    setState("guest");
    navigate("/login");
  }

  const chatMatch = useMemo(() => path.match(/^\/chats\/(-?\d+)(?:\/(settings|admins|bans|spam-log|audit))?$/), [path]);
  const userMatch = useMemo(() => path.match(/^\/users\/(\d+)$/), [path]);

  if (state === "loading") return <div className="boot-screen"><span className="brand-mark large">CG</span><div className="boot-lines"><i /><i /><i /></div><p>Загружаю панель</p></div>;
  if (state === "guest") return <LoginPage onAuthenticated={() => load()} />;
  if (state === "error" || !user) return <div className="fatal-state"><WarningIcon size={38} weight="thin" /><strong>Не удалось открыть панель</strong><p>Сервер не отвечает. Попробуйте ещё раз.</p><button className="button primary" onClick={() => void load()}>Повторить</button></div>;

  let page;
  if (chatMatch) {
    const chatId = Number(chatMatch[1]);
    page = <ChatRoute key={`${user.id}:${chatId}`} chatId={chatId} userId={user.id} definitions={definitions} initialTab={chatTabFromPath(chatMatch[2])} onBack={() => navigate("/chats")} onTabChange={(tab) => navigate(`/chats/${chatId}${chatTabPaths[tab]}`)} onOpenUser={(id) => navigate(`/users/${id}`)} setToast={setToast} />;
  } else if (userMatch) {
    page = <UserDetailPage key={`${user.id}:${userMatch[1]}`} userId={Number(userMatch[1])} currentUser={user} chats={chats} onBack={() => navigate("/users")} onOpenChat={(id) => navigate(`/chats/${id}`)} setToast={setToast} />;
  } else if (path === "/chats" || path === "/admin/all-chats") {
    page = <ChatsPage definitions={definitions} onOpenChat={(id) => navigate(`/chats/${id}`)} setToast={setToast} />;
  } else if (path === "/users") {
    page = <UsersPage onOpenUser={(id) => navigate(`/users/${id}`)} />;
  } else if (path === "/logs" || path === "/admin/logs") {
    page = <LogsPage user={user} chats={chats} onOpenUser={(id) => navigate(`/users/${id}`)} onOpenChat={(id) => navigate(`/chats/${id}`)} />;
  } else if (path === "/votes") {
    page = <VotesPage onOpenUser={(id) => navigate(`/users/${id}`)} onOpenChat={(id) => navigate(`/chats/${id}`)} setToast={setToast} />;
  } else if (path === "/admin/patterns" && user.is_super_admin) {
    page = <PatternsPage setToast={setToast} />;
  } else if (path === "/admin/image-cache" && user.is_super_admin) {
    page = <ImageCachePage setToast={setToast} />;
  } else if (["/admin/lists", "/admin/spamers", "/admin/approved", "/admin/administrators"].includes(path) && user.is_super_admin) {
    const kinds: Record<string, ListKind> = { "/admin/approved": "approved", "/admin/administrators": "administrators" };
    page = <ListsPage initialKind={kinds[path] || "spamers"} onKindChange={(kind) => navigate(`/admin/${kind}`)} onOpenUser={(id) => navigate(`/users/${id}`)} setToast={setToast} />;
  } else if (["/", "/dashboard", "/login", "/admin"].includes(path)) {
    page = <DashboardPage user={user} chatOptions={chatOptions} onNavigate={navigate} />;
  } else {
    page = <NotFoundPage onHome={() => navigate("/dashboard")} />;
  }

  return (
    <>
      <Shell user={user} section={sectionFor(path)} dark={dark} onNavigate={navigate} onToggleTheme={() => setDark((value) => !value)} onLogout={() => void logout()}>{logoutError && <ErrorState message={logoutError} onRetry={() => void logout()} />}<Suspense fallback={<SkeletonRows rows={8} columns={5} />}>{page}</Suspense></Shell>
      <Toast toast={toast} />
    </>
  );
}

function ChatRoute(props: {
  chatId: number;
  userId: number;
  definitions: SettingDefinition[];
  initialTab: ChatTab;
  onBack: () => void;
  onTabChange: (tab: ChatTab) => void;
  onOpenUser: (id: number) => void;
  setToast: (toast: ToastData) => void;
}) {
  const resource = useResource((signal) => api.chat(props.chatId, signal), [props.chatId], "Не удалось загрузить чат");
  if (resource.error) return <ErrorState message={resource.error} onRetry={resource.reload} />;
  if (!resource.data) return <SkeletonRows rows={8} columns={5} />;
  return <ChatDetailPage {...props} initialChat={resource.data} onUpdated={resource.setData} />;
}
